import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import ForgotPasswordForm from '../components/ForgotPasswordForm';
import LoadingState from '../components/LoadingState';
import LoginForm from '../components/LoginForm';
import SignUpForm from '../components/SignupForm';
import valerieImage from '../images/valerie_accolade.png';

/**
 * Light ("white mode") version of the auth screens.
 * If you already have the dark components, you can swap them out or
 * pass a prop to choose classes. This file keeps it simple and just
 * ships dedicated light components.
 */

type AuthMode = 'login' | 'signup' | 'forgot-password';

export default function AuthPage() {
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const { session, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || undefined;

  // Show loading state while determining authentication status
  if (loading) {
    return <LoadingState />;
  }

  // Redirect to home if already authenticated
  if (session) {
    return <Navigate to="/" replace />;
  }

  const handleToggleMode = () => {
    if (authMode === 'login') {
      setAuthMode('signup');
    } else if (authMode === 'signup') {
      setAuthMode('login');
    } else {
      setAuthMode('login');
    }
  };

  const handleForgotPassword = () => {
    setAuthMode('forgot-password');
  };

  return (
    <div className="min-h-screen w-full bg-white text-zinc-900 flex">
      {/* Left panel */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 lg:px-20 bg-gray-50 shadow-lg">
        <div className="w-full max-w-md">
          {authMode === 'login' ? (
            <LoginForm
              onToggleMode={handleToggleMode}
              onForgotPassword={handleForgotPassword}
              redirect={redirect}
            />
          ) : authMode === 'signup' ? (
            <SignUpForm onToggleMode={handleToggleMode} redirect={redirect} />
          ) : (
            <ForgotPasswordForm onToggleMode={handleToggleMode} />
          )}
        </div>
      </div>

      {/* Right panel: quote */}
      <aside className="hidden xl:flex flex-[1.5] items-center justify-center p-10 bg-white">
        <div className="max-w-lg text-zinc-700">
          <p className="text-3xl font-medium leading-snug text-zinc-900">
            "Notewell lets my caseworkers <span className="font-bold">review, not write</span>. It
            keeps our clients front and center and lightens the paperwork load so my team can
            actually breathe."
          </p>
          <div className="mt-6 flex items-center gap-3">
            <img src={valerieImage} alt="Valerie H." className="h-14 w-14 rounded-full" />
            <div>
              <p className="text-sm text-zinc-800">Valerie H.</p>
              <p className="text-xs text-zinc-600">Executive Director</p>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
